/**
 * Dialog / Sheet — 왓퀴즈 모달 프리미티브 (토스 스타일)
 *
 * <Dialog open onClose title description size="sm|md|lg" footer dismissible>
 *   본문
 * </Dialog>
 *
 * 레이아웃
 *   Dialog : >600px 중앙 다이얼로그(16px 라운드) / ≤600px 바텀시트(상단 16px 라운드, safe-area)
 *   Sheet  : 모든 폭에서 바텀시트 (메뉴·프로모션·도움말처럼 "화면에서 솟아오르는" 콘텐츠)
 *
 * Props
 *   open, onClose
 *   title           : 제목 (h2, aria-labelledby). hideTitle이면 시각적으로 숨기고 접근성 이름으로만 쓴다
 *   description     : 제목 아래 설명 (aria-describedby)
 *   eyebrow / icon  : 제목 위 캡션 / 아이콘 (마케팅·안내 모달)
 *   align           : 'start' | 'center' — 헤더 정렬
 *   size            : 'sm'(400) | 'md'(520) | 'lg'(760) — 데스크톱 최대 폭
 *   footer          : 하단 액션 영역 (ModalActions 권장)
 *   dismissible     : false면 ESC·스크림·닫기 버튼으로 닫히지 않는다 (로딩·필수 확인)
 *   showClose       : 우상단 닫기 버튼 (기본 true, dismissible일 때만 보임)
 *   dragHandle      : 바텀시트 핸들 + pull-to-dismiss (핸들이 있으면 반드시 끌어서 닫힌다)
 *   initialFocus    : 'panel'(기본) | 'first' | ref — 열릴 때 포커스할 대상
 *   panelRef        : 패널 DOM을 밖에서 다뤄야 할 때
 *   flush           : 본문 패딩 제거 (캔버스·리스트가 가장자리까지 차는 경우)
 *   zIndex          : 기본 var(--whatquiz-z-modal, 10000)
 *   portal          : 기본 true (document.body)
 *   scrollLock      : 기본 true (ref-count 잠금 → 겹친 모달이 먼저 풀지 않는다)
 *   registerLayer   : 기본 true (열려 있는 동안 LayerProvider에 등록 → 토스트가 기다린다)
 *   role            : 'dialog' | 'alertdialog'
 *   aria-busy       : 패널에 aria-busy를 붙인다 (진행 중인 확인 대화상자 등)
 *
 * 접근성
 *   role="dialog" aria-modal aria-labelledby aria-describedby, 포커스 트랩, 열릴 때 포커스 이동,
 *   닫힐 때 원래 위치로 복귀, ESC, 스크림 클릭(패널 안에서 시작한 드래그는 무시), 바디 스크롤 잠금.
 *
 * 조합용 헬퍼: DialogHeader / DialogBody / DialogFooter
 */

import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import useAppScrollLock from '../../hooks/useAppScrollLock';
import { useLayer, useRegisterLayer } from './LayerContext';
import './Dialog.css';

export const DIALOG_SHEET_MEDIA_QUERY = '(max-width: 600px)';
export const DIALOG_SIZES = ['sm', 'md', 'lg'];
const SHEET_DISMISS_PX = 80;

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const canUseDom = () => typeof document !== 'undefined';
const canUseMatchMedia = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const readSheetMatch = () => (canUseMatchMedia()
  ? Boolean(window.matchMedia(DIALOG_SHEET_MEDIA_QUERY).matches)
  : false);

/**
 * 화면 폭에 따라 바텀시트/다이얼로그를 고른다.
 * presentation: 'auto'(폭 기준) | 'dialog' | 'sheet'
 * matchMedia가 없는 환경(구형 브라우저·JSDOM)은 다이얼로그.
 */
export const useSheetLayout = (open, presentation = 'auto') => {
  const [matches, setMatches] = useState(readSheetMatch);

  useEffect(() => {
    if (!open || presentation !== 'auto' || !canUseMatchMedia()) return undefined;
    const mediaQuery = window.matchMedia(DIALOG_SHEET_MEDIA_QUERY);
    const handleChange = (event) => setMatches(Boolean(event.matches));
    setMatches(Boolean(mediaQuery.matches));

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
    return undefined;
  }, [open, presentation]);

  if (presentation === 'sheet') return true;
  if (presentation === 'dialog') return false;
  return matches;
};

const joinClassNames = (...values) => values.filter(Boolean).join(' ');

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
    <path
      d="M6 6l12 12M18 6L6 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// ---------------------------------------------------------------------------
// 조합용 헬퍼
// ---------------------------------------------------------------------------

export const DialogHeader = ({
  align = 'start',
  children,
  className,
  description,
  descriptionId,
  eyebrow,
  hideTitle = false,
  icon,
  testId,
  title,
  titleId,
}) => {
  const hasDescription = description !== null && description !== undefined && description !== '';
  return (
    <header
      className={joinClassNames(
        'whatquiz-dialog__header',
        align === 'center' && 'whatquiz-dialog__header--center',
        className,
      )}
      data-testid={testId}
    >
      {icon && <div className="whatquiz-dialog__icon">{icon}</div>}
      {eyebrow && <span className="whatquiz-dialog__eyebrow">{eyebrow}</span>}
      {title !== undefined && title !== null && (
        <h2
          className={hideTitle ? 'whatquiz-visually-hidden' : 'whatquiz-dialog__title'}
          id={titleId}
        >
          {title}
        </h2>
      )}
      {hasDescription && (
        typeof description === 'string' ? (
          <p className="whatquiz-dialog__description" id={descriptionId}>{description}</p>
        ) : (
          <div className="whatquiz-dialog__description" id={descriptionId}>{description}</div>
        )
      )}
      {children}
    </header>
  );
};

export const DialogBody = ({ children, className, flush = false, testId }) => (
  <div
    className={joinClassNames(
      'whatquiz-dialog__body',
      flush && 'whatquiz-dialog__body--flush',
      className,
    )}
    data-testid={testId}
  >
    {children}
  </div>
);

export const DialogFooter = ({ children, className, testId }) => (
  <footer className={joinClassNames('whatquiz-dialog__footer', className)} data-testid={testId}>
    {children}
  </footer>
);

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

const Dialog = ({
  align = 'start',
  'aria-busy': ariaBusy,
  'aria-label': ariaLabel,
  bodyClassName,
  children,
  className,
  closeLabel = '닫기',
  description = null,
  dismissible = true,
  dragHandle = false,
  eyebrow = null,
  flush = false,
  footer = null,
  hideTitle = false,
  icon = null,
  initialFocus = 'panel',
  onClose,
  open = false,
  panelClassName,
  panelRef: externalPanelRef = null,
  portal = true,
  presentation = 'auto',
  registerLayer = true,
  role = 'dialog',
  scrollLock = true,
  showClose = true,
  size = 'md',
  testId = 'whatquiz-dialog',
  title = null,
  zIndex,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const mouseDownTargetRef = useRef(null);
  const touchStartYRef = useRef(null);

  // 리스너는 open 생명주기에만 묶고, 최신 값은 ref로 읽는다.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;
  const initialFocusRef = useRef(initialFocus);
  initialFocusRef.current = initialFocus;

  const isSheet = useSheetLayout(open, presentation);
  const layer = useLayer();
  const layerId = useRegisterLayer(Boolean(open && registerLayer));
  const layerRef = useRef(layer);
  layerRef.current = layer;
  useAppScrollLock(Boolean(open && scrollLock));

  const setPanelNode = useCallback((node) => {
    panelRef.current = node;
    if (!externalPanelRef) return;
    if (typeof externalPanelRef === 'function') externalPanelRef(node);
    else externalPanelRef.current = node;
  }, [externalPanelRef]);

  const requestClose = useCallback(() => {
    if (!dismissibleRef.current) return;
    onCloseRef.current?.();
  }, []);

  // 키·포커스는 페인트 전에 붙인다. 로딩(닫기 불가) → 상세처럼 같은 턴에
  // 대화상자가 바뀌면 useEffect 리스너가 한 박자 남아 ESC가 먹지 않는다.
  useLayoutEffect(() => {
    if (!open || !canUseDom()) return undefined;

    restoreFocusRef.current = document.activeElement;
    const panel = panelRef.current;
    const focusInitial = () => {
      const target = initialFocusRef.current;
      if (target && typeof target === 'object' && target.current?.focus) {
        target.current.focus({ preventScroll: true });
        return;
      }
      if (target === 'first' && panel) {
        const first = panel.querySelector(FOCUSABLE_SELECTOR);
        if (first) {
          first.focus({ preventScroll: true });
          return;
        }
      }
      panel?.focus?.({ preventScroll: true });
    };
    focusInitial();

    const isTopLayer = () => {
      const current = layerRef.current;
      if (!current.hasProvider || !registerLayer) return true;
      return current.topLayerId === layerId;
    };

    const handleKeyDown = (event) => {
      if (event.defaultPrevented || !panelRef.current) return;
      const target = event.target;
      // 다른 대화상자 안에서 눌린 키는 그쪽이 처리한다 (형제 모달·중첩 모달)
      if (target instanceof Element && !panelRef.current.contains(target)) {
        const otherDialog = target.closest('[role="dialog"],[role="alertdialog"]');
        if (otherDialog && otherDialog !== panelRef.current) return;
      }
      if (!isTopLayer()) return;

      if (event.key === 'Escape') {
        if (!dismissibleRef.current) return;
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const insidePanel = panelRef.current.contains(active) && active !== panelRef.current;

      if (!insidePanel) {
        // 패널 자체나 바깥에 포커스가 있으면 순환의 시작점으로 보낸다
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const previous = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [open, layerId, registerLayer]);

  if (!open || !canUseDom()) return null;

  const hasTitle = title !== null && title !== undefined && title !== '';
  const hasDescription = description !== null && description !== undefined && description !== '';
  const hasVisibleHeader = (hasTitle && !hideTitle) || hasDescription || Boolean(eyebrow) || Boolean(icon);
  const hasBody = children !== null && children !== undefined && children !== false;
  const showCloseButton = Boolean(dismissible && showClose && onClose);
  const showHandle = Boolean(isSheet && dragHandle && dismissible);

  const handleBackdropMouseDown = (event) => {
    mouseDownTargetRef.current = event.target;
  };

  const handleBackdropClick = (event) => {
    const pressedOn = mouseDownTargetRef.current;
    mouseDownTargetRef.current = null;
    if (event.target !== event.currentTarget) return;
    // 패널 안에서 드래그를 시작해 배경에서 손을 뗀 경우는 닫지 않는다
    if (pressedOn && pressedOn !== event.currentTarget) return;
    requestClose();
  };

  const resetSheetPosition = () => {
    const panel = panelRef.current;
    if (!panel) return;
    panel.style.transition = 'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease';
    panel.style.transform = '';
    panel.style.opacity = '';
  };

  const handleGripTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartYRef.current = touch.clientY;
  };

  const handleGripTouchMove = (event) => {
    const panel = panelRef.current;
    const touch = event.touches?.[0];
    if (!panel || !touch || touchStartYRef.current === null) return;
    const deltaY = touch.clientY - touchStartYRef.current;
    if (deltaY <= 0) return;
    panel.style.transition = 'none';
    panel.style.transform = `translateY(${deltaY}px)`;
    panel.style.opacity = String(Math.max(0.55, 1 - deltaY / 320));
  };

  const handleGripTouchEnd = (event) => {
    const startY = touchStartYRef.current;
    touchStartYRef.current = null;
    const touch = event.changedTouches?.[0];
    if (startY === null || !touch) {
      resetSheetPosition();
      return;
    }
    if (touch.clientY - startY > SHEET_DISMISS_PX) {
      resetSheetPosition();
      requestClose();
      return;
    }
    resetSheetPosition();
  };

  const overlayClassName = joinClassNames(
    'whatquiz-dialog__overlay',
    isSheet ? 'whatquiz-dialog__overlay--sheet' : 'whatquiz-dialog__overlay--dialog',
    className,
  );

  const panelClassNames = joinClassNames(
    'whatquiz-dialog__panel',
    `whatquiz-dialog__panel--${DIALOG_SIZES.includes(size) ? size : 'md'}`,
    isSheet ? 'whatquiz-dialog__panel--sheet' : 'whatquiz-dialog__panel--dialog',
    panelClassName,
  );

  const content = (
    <div
      className={overlayClassName}
      data-testid={`${testId}-overlay`}
      onClick={handleBackdropClick}
      onMouseDown={handleBackdropMouseDown}
      style={zIndex !== undefined ? { zIndex } : undefined}
    >
      <div
        aria-busy={ariaBusy || undefined}
        aria-describedby={hasDescription ? descriptionId : undefined}
        aria-label={hasTitle ? undefined : ariaLabel}
        aria-labelledby={hasTitle ? titleId : undefined}
        aria-modal="true"
        className={panelClassNames}
        data-testid={testId}
        ref={setPanelNode}
        role={role}
        tabIndex={-1}
      >
        {showHandle && (
          <div
            aria-hidden="true"
            className="whatquiz-dialog__grip"
            data-testid={`${testId}-grip`}
            onTouchCancel={handleGripTouchEnd}
            onTouchEnd={handleGripTouchEnd}
            onTouchMove={handleGripTouchMove}
            onTouchStart={handleGripTouchStart}
          >
            <span />
          </div>
        )}
        {showCloseButton && (
          <button
            aria-label={closeLabel}
            className="whatquiz-dialog__close"
            data-testid={`${testId}-close`}
            onClick={onClose}
            type="button"
          >
            <CloseIcon />
          </button>
        )}
        {hasVisibleHeader ? (
          <DialogHeader
            align={align}
            description={description}
            descriptionId={descriptionId}
            eyebrow={eyebrow}
            hideTitle={hideTitle}
            icon={icon}
            testId={`${testId}-header`}
            title={hasTitle ? title : undefined}
            titleId={titleId}
          />
        ) : hasTitle && (
          <h2 className="whatquiz-visually-hidden" id={titleId}>{title}</h2>
        )}
        {hasBody && (
          <DialogBody
            className={joinClassNames(footer && 'whatquiz-dialog__body--before-footer', bodyClassName)}
            flush={flush}
            testId={`${testId}-body`}
          >
            {children}
          </DialogBody>
        )}
        {footer && <DialogFooter testId={`${testId}-footer`}>{footer}</DialogFooter>}
      </div>
    </div>
  );

  return portal ? createPortal(content, document.body) : content;
};

/**
 * Sheet — 모든 화면 폭에서 바텀시트로 뜨는 Dialog.
 * 메뉴·옵션 선택·도움말·프로모션처럼 "지금 화면과 이어지는" 콘텐츠에 쓴다.
 */
export const Sheet = (props) => <Dialog {...props} presentation="sheet" />;

export default Dialog;
